import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";

// The desktop app's ForgotPassword screen triggers a backend email containing
// a link to https://mytokenguard.com/reset-password?token=... -- this page is
// what that link opens. It's a plain website page (not part of the desktop
// app's own React tree), so after a successful reset the user just closes
// this tab and switches back to the desktop app to log in.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://api.mytokenguard.com";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/password/reset`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || "Invalid or expired link");
      }
      setDone(true);
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="section" style={{ paddingTop: 80, minHeight: "60vh", display: "flex", alignItems: "center" }}>
      <div className="container" style={{ maxWidth: 440, margin: "0 auto" }}>
        <div
          style={{
            background: "var(--card-surface)",
            border: "1px solid var(--stone)",
            borderRadius: "var(--radius-lg)",
            padding: 36,
          }}
        >
          {!token ? (
            <InvalidLinkState />
          ) : done ? (
            <SuccessState />
          ) : (
            <>
              <h1 style={{ fontSize: 24, marginBottom: 8 }}>Set new password</h1>
              <p style={{ fontSize: 14, color: "var(--slate)", marginBottom: 24 }}>
                Choose a new password for your TokenGuard account.
              </p>
              <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Field
                  label="New password"
                  type="password"
                  value={password}
                  onChange={setPassword}
                  autoComplete="new-password"
                  required
                />
                <Field
                  label="Confirm new password"
                  type="password"
                  value={confirm}
                  onChange={setConfirm}
                  autoComplete="new-password"
                  required
                />
                {error && (
                  <p style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--error)", fontSize: 13.5 }}>
                    <XCircle size={16} /> {error}
                  </p>
                )}
                <button type="submit" className="btn btn-primary" style={{ width: "100%" }} disabled={loading}>
                  {loading ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", value, onChange, required, autoComplete }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <label style={{ fontSize: 12.5, fontWeight: 600, color: "var(--slate)" }}>{label}</label>
      <input
        type={type}
        required={required}
        autoComplete={autoComplete}
        minLength={8}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: "100%",
          height: 44,
          padding: "0 14px",
          borderRadius: "var(--radius-sm)",
          border: "1.5px solid var(--stone)",
          background: "var(--elevated-surface)",
          fontFamily: "var(--font-body)",
          fontSize: 14,
        }}
      />
    </div>
  );
}

function SuccessState() {
  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: "rgba(46,125,107,0.12)",
          color: "var(--success)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <CheckCircle2 size={24} />
      </div>
      <h1 style={{ fontSize: 20, marginBottom: 8 }}>Password updated</h1>
      <p style={{ fontSize: 14, color: "var(--slate)", lineHeight: 1.6 }}>
        You can close this tab now and log in from the TokenGuard app with your new password.
      </p>
    </div>
  );
}

function InvalidLinkState() {
  return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: "rgba(192,57,43,0.12)",
          color: "var(--error)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 16px",
        }}
      >
        <XCircle size={24} />
      </div>
      <h1 style={{ fontSize: 20, marginBottom: 8 }}>Invalid link</h1>
      <p style={{ fontSize: 14, color: "var(--slate)", lineHeight: 1.6 }}>
        This password reset link is missing or malformed. Request a new one from the TokenGuard app's login screen.
      </p>
    </div>
  );
}
